/**
 * 浏览器端 HTTP 适配器（使用 fetch）
 */

import type { HttpRequest, HttpResponse } from '../../../types/api'
import { CsdError } from '../../../types/error'
import { ERROR_CODES } from '../../../constants/errors'

function hasHeader(headers: Record<string, string>, headerName: string): boolean {
  const normalizedName = headerName.toLowerCase()
  return Object.keys(headers).some(key => key.toLowerCase() === normalizedName)
}

function isBodyInit(data: unknown): data is BodyInit {
  return (
    typeof data === 'string' ||
    (typeof URLSearchParams !== 'undefined' && data instanceof URLSearchParams) ||
    (typeof FormData !== 'undefined' && data instanceof FormData) ||
    (typeof Blob !== 'undefined' && data instanceof Blob) ||
    data instanceof ArrayBuffer ||
    ArrayBuffer.isView(data)
  )
}

function buildRequestBody(data: unknown, headers: Record<string, string>): BodyInit | undefined {
  if (data === undefined || data === null) {
    return undefined
  }

  if (isBodyInit(data)) {
    return data
  }

  if (!hasHeader(headers, 'Content-Type')) {
    headers['Content-Type'] = 'application/json'
  }

  return JSON.stringify(data)
}

export async function browserAdapter(request: HttpRequest): Promise<HttpResponse> {
  const { url, method = 'GET', data, timeout = 30000, baseURL = '' } = request
  const requestHeaders = request.headers || {}

  const fullUrl = baseURL ? `${baseURL}${url}` : url

  // 创建 AbortController 用于超时控制
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), timeout)

  try {
    // 构建请求头，普通对象默认使用 JSON；字符串/FormData/URLSearchParams 原样发送
    const headers: Record<string, string> = { ...requestHeaders }
    const body = buildRequestBody(data, headers)

    const response = await fetch(fullUrl, {
      method,
      headers: headers as HeadersInit,
      body,
      signal: controller.signal
    })

    clearTimeout(timeoutId)

    // 解析响应
    let responseData: any
    const contentType = response.headers.get('content-type')

    if (contentType?.includes('application/json')) {
      responseData = await response.json()
    } else {
      responseData = await response.text()
    }

    // 转换 headers 为普通对象
    const responseHeaders: Record<string, string> = {}
    response.headers.forEach((value, key) => {
      responseHeaders[key] = value
    })

    // 构造响应对象
    const httpResponse: HttpResponse = {
      data: responseData,
      status: response.status,
      statusText: response.statusText,
      headers: responseHeaders,
      config: request
    }

    // 检查 HTTP 状态码
    if (!response.ok) {
      throw new CsdError(
        `HTTP Error: ${response.status} ${response.statusText}`,
        ERROR_CODES.NETWORK_ERROR,
        httpResponse
      )
    }

    return httpResponse
  } catch (error: any) {
    clearTimeout(timeoutId)

    // 处理超时错误
    if (error.name === 'AbortError') {
      throw new CsdError('Request timeout', ERROR_CODES.TIMEOUT_ERROR, { config: request })
    }

    // 处理网络错误
    if (error instanceof TypeError) {
      throw new CsdError('Network error', ERROR_CODES.NETWORK_ERROR, { config: request })
    }

    // 重新抛出其他错误
    throw error
  }
}
